import { useAuth0 } from '@auth0/auth0-react';
import type { Private } from '@seek/indie-api-types';
import { useQuery } from '@tanstack/react-query';
import { createContext, useContext, useMemo, type ReactNode } from 'react';
import { useApi } from '#src/api/index.ts';
import { usePermissions } from '#src/hooks/auth.tsx';

interface OrganizationProviderProps {
  children: ReactNode;
}

interface OrganizationContext {
  organizations: Private.Auth0UserOrganizationPayload[];
  currentOrganization?: Private.Auth0UserOrganizationPayload;
  users: Private.Auth0UserPayload[];
  invitations: Private.Auth0OrganizationInvitationPayload[];
  loading: boolean;
  refresh: () => Promise<void>;
  error?: string;
  setNewRole: (newDetails: Private.Auth0UserRoleChangeInput) => Promise<void>;
  removeUser: (userId: string) => Promise<void>;
  removeSelf: (organizationId: string) => Promise<void>;
  deleteInvite: (inviteId: string) => Promise<void>;
  resetMfa: (userId: string) => Promise<void>;
}

const OrganizationContext = createContext<OrganizationContext>({
  organizations: [],
  currentOrganization: undefined,
  users: [],
  invitations: [],
  loading: false,
  refresh: async () => {
    throw new Error('refresh must be used within a OrganizationProvider');
  },
  error: '',
  setNewRole: async () => {
    throw new Error('setNewRole must be used within a OrganizationProvider');
  },
  removeUser: async () => {
    throw new Error('removeUser must be used within a OrganizationProvider');
  },
  removeSelf: async () => {
    throw new Error('removeSelf must be used within a OrganizationProvider');
  },
  deleteInvite: async () => {
    throw new Error('deleteInvite must be used within a OrganizationProvider');
  },
  resetMfa: async () => {
    throw new Error('resetMfa must be used within a OrganizationProvider');
  },
});

export const useOrganizations = () => {
  const context = useContext(OrganizationContext);
  return context;
};

export const OrganizationProvider = ({
  children,
}: OrganizationProviderProps) => {
  const api = useApi();
  const { isAuthenticated } = useAuth0();
  const { permissions, integrationPartnerUuid } = usePermissions();
  const hasViewUsersPermission = permissions.includes('query:users');

  const {
    data: organizations = [],
    isLoading: loadingOrganizations,
    error: organizationsError,
    refetch: refetchOrganizations,
  } = useQuery({
    queryKey: ['organizations', isAuthenticated, integrationPartnerUuid, api],
    queryFn: ({ signal }) =>
      isAuthenticated ? api.organizations.listOrganizations(signal) : [],
  });

  const {
    data: users = [],
    isLoading: loadingUsers,
    error: usersError,
    refetch: refetchUsers,
  } = useQuery({
    queryKey: ['users', hasViewUsersPermission, integrationPartnerUuid, api],
    queryFn: ({ signal }) =>
      hasViewUsersPermission ? api.organizations.listUsers(signal) : [],
  });

  const {
    data: invitations = [],
    isLoading: loadingInvitations,
    error: invitationsError,
    refetch: refetchInvitations,
  } = useQuery({
    queryKey: [
      'invitations',
      hasViewUsersPermission,
      integrationPartnerUuid,
      api,
    ],
    queryFn: ({ signal }) =>
      hasViewUsersPermission ? api.organizations.listInvitations(signal) : [],
  });

  const value = useMemo(() => {
    const setNewRole = async (input: Private.Auth0UserRoleChangeInput) => {
      await api.organizations.changeRole(input);
      await refetchUsers();
    };

    const removeUser = async (userId: string) => {
      await api.organizations.removeUser(userId);
      await refetchUsers();
    };

    const removeSelf = async (organizationId: string) => {
      await api.organizations.removeSelf(organizationId);
      await refetchOrganizations();
    };

    const deleteInvite = async (inviteId: string) => {
      await api.organizations.deleteInvite(inviteId);
      await refetchInvitations();
    };

    const resetMfa = async (userId: string) => {
      await api.organizations.resetMfa(userId);
      await refetchUsers();
    };

    const firstError = organizationsError ?? usersError ?? invitationsError;
    return {
      users,
      invitations,
      organizations,
      loading: loadingOrganizations || loadingUsers || loadingInvitations,
      refresh: async () => {
        await Promise.all([
          refetchOrganizations(),
          refetchUsers(),
          refetchInvitations(),
        ]);
      },
      error: firstError ? String(firstError) : undefined,
      setNewRole,
      removeUser,
      removeSelf,
      deleteInvite,
      resetMfa,
    };
  }, [
    api,
    users,
    organizations,
    invitations,
    loadingOrganizations,
    loadingUsers,
    loadingInvitations,
    organizationsError,
    usersError,
    invitationsError,
    refetchOrganizations,
    refetchUsers,
    refetchInvitations,
  ]);

  return (
    <OrganizationContext.Provider value={value}>
      {children}
    </OrganizationContext.Provider>
  );
};
